import React from 'react';
import { Link } from 'react-router-dom';
import { FaPlay, FaDownload, FaHeart, FaQuestionCircle, FaCheckCircle } from 'react-icons/fa';
import './HomepageSEO.css';

const HomepageSEO = ({ sounds = [], categories = [] }) => {
  // Get popular sounds (first 8 sounds as example)
  const popularSounds = sounds.slice(0, 8);
  
  // Get unique categories

  
  // FAQ data for rich content
  const faqData = [
    {
      question: "Tous les sons sont-ils vraiment gratuits ?",
      answer: "Oui, tous les sons proposés sur SoundNest sont gratuits. Ils proviennent de LaSonotheque.org, qui les distribue sous licence libre. Nous vous recommandons de consulter leurs conditions d'utilisation pour connaître tous les détails."
    },
    {
      question: "Quels formats audio sont disponibles ?",
      answer: "Nous proposons tous nos sons en deux formats : MP3 (compressé, idéal pour le web) et WAV (haute qualité, parfait pour la production professionnelle). Vous pouvez choisir le format qui convient le mieux à votre projet."
    },
    {
      question: "Puis-je utiliser ces sons dans mes vidéos YouTube ?",
      answer: "Oui, tous nos sons peuvent être utilisés librement dans vos vidéos YouTube, podcasts, films, jeux vidéo et tout autre projet créatif. Aucune attribution n'est requise, mais elle est toujours appréciée !"
    },
    {
      question: "Comment télécharger un son ?",
      answer: "C'est très simple ! Cliquez sur le son qui vous intéresse, écoutez-le pour vous assurer qu'il correspond à vos besoins, puis cliquez sur le bouton de téléchargement MP3 ou WAV selon votre préférence."
    },
    {
      question: "Ajoutez-vous régulièrement de nouveaux sons ?",
      answer: "Nous enrichissons constamment notre bibliothèque avec de nouveaux sons de qualité professionnelle. Ajoutez vos sons préférés aux favoris pour ne rien manquer des nouveautés dans vos catégories favorites !"
    }
  ];

  return (
    <div className="homepage-seo">
      {/* Hero Section with Rich Content */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            SoundNest - Votre Bibliothèque de Sons Gratuits et Libres de Droits
          </h1>
          <p className="hero-description">
            Découvrez une vaste collection de <strong>sons gratuits et libres de droits</strong> de qualité professionnelle. 
            Téléchargez des effets sonores, ambiances et bruits pour enrichir vos projets créatifs, 
            vidéos, podcasts, jeux vidéo et créations multimédias.
          </p>
          <div className="hero-features">
            <div className="feature-item">
              <FaDownload className="feature-icon" />
              <span>Téléchargement gratuit</span>
            </div>
            <div className="feature-item">
              <FaCheckCircle className="feature-icon" />
              <span>Libre de droits</span>
            </div>
            <div className="feature-item">
              <FaPlay className="feature-icon" />
              <span>Formats MP3 & WAV</span>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Sounds Section */}
      <section className="popular-sounds">
        <h2 className="section-title">Sons les Plus Populaires</h2>
        <p className="section-description">
          Découvrez les sons les plus téléchargés par notre communauté de créateurs
        </p>
        
        <div className="sounds-grid">
          {popularSounds.map((sound) => {
            const slug = sound.slug || sound.title
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .replace(/[^a-z0-9\s-]/g, '')
              .replace(/\s+/g, '-')
              .replace(/-+/g, '-')
              .trim();
              
            return (
              <article key={sound.id} className="sound-card">
                <div className="sound-card-header">
                  <h3 className="sound-card-title">
                    <Link to={`/sons/${slug}`}>{sound.title}</Link>
                  </h3>
                  <span className="sound-card-theme">{sound.theme}</span>
                </div>
                <div className="sound-card-meta">
                  <span className="sound-duration">{sound.duration}</span>
                  <Link 
                    to={`/sons/${slug}`} 
                    className="sound-card-link"
                    aria-label={`Écouter ${sound.title}`}
                  >
                    <FaPlay /> Écouter
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </section>



      {/* FAQ Section for Rich Content */}
      <section className="faq-section">
        <h2 className="section-title">Questions Fréquemment Posées</h2>
        <p className="section-description">
          Tout ce que vous devez savoir sur l'utilisation de nos sons gratuits
        </p>
        
        <div className="faq-list">
          {faqData.map((faq, index) => (
            <article key={index} className="faq-item">
              <h3 className="faq-question">
                <FaQuestionCircle className="faq-icon" />
                {faq.question}
              </h3>
              <p className="faq-answer">{faq.answer}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Usage Examples Section */}
      <section className="usage-section">
        <h2 className="section-title">Utilisations Recommandées</h2>
        <div className="usage-grid">
          <div className="usage-item">
            <h3>Création Vidéo</h3>
            <p>Enrichissez vos vidéos YouTube, films et documentaires avec des effets sonores professionnels</p>
          </div>
          <div className="usage-item">
            <h3>Podcasts & Audio</h3>
            <p>Ajoutez des transitions, ambiances et effets pour captiver votre audience</p>
          </div>
          <div className="usage-item">
            <h3>Jeux Vidéo</h3>
            <p>Intégrez des sons d'impact, d'ambiance et d'effets spéciaux dans vos créations ludiques</p>
          </div>
          <div className="usage-item">
            <h3>Relaxation & Méditation</h3>
            <p>Utilisez nos ambiances naturelles pour créer des expériences apaisantes</p>
          </div>
          <div className="usage-item">
            <h3>Montage Audio</h3>
            <p>Perfectionnez vos productions avec des transitions et effets de qualité studio</p>
          </div>
          <div className="usage-item">
            <h3>Contenu ASMR</h3>
            <p>Découvrez des sons doux et relaxants parfaits pour vos créations ASMR</p>
          </div>
        </div>
      </section>

      {/* Keywords Section for SEO */}
      <section className="keywords-section">
        <h2 className="sr-only">Mots-clés et termes de recherche</h2>
        <div className="keywords-content">
          <p>
            <strong>Sons gratuits</strong>, <strong>effets sonores libres de droits</strong>, 
            <strong>musique gratuite</strong>, <strong>bruits d'ambiance</strong>, 
            <strong>sons pour montage vidéo</strong>, <strong>effets audio gratuits</strong>, 
            <strong>sons de relaxation</strong>, <strong>bruits de nature</strong>, 
            <strong>effets cartoon</strong>, <strong>sons d'impact</strong>, 
            <strong>transitions audio</strong>, <strong>ambiances sonores</strong>, 
            <strong>sons ASMR</strong>, <strong>effets de swoosh</strong>, 
            <strong>explosions audio</strong>, <strong>sons pour podcast</strong>
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomepageSEO;
